import { useMemo } from 'react';
import styled from 'styled-components';

import { useInfiniteQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import ErroredAttachmentsBanner from 'components/shared/ai/ErroredAttachmentsBanner';
import DataTable from 'components/shared/DataTable';
import DocumentLink from 'components/shared/DocumentLink';
import useIsMobile from 'hooks/useIsMobile';
import { dateAtTime } from 'lib/date';
import Attachment from 'models/Attachment';
import { attachmentsQueryKeys, indexWorkflowAttachments } from 'services/api/attachments';
import colors from 'styles/theme/colors';
import { Body, BodySmallBold } from 'styles/typography';
import ErrorWarningIcon from 'svg/ErrorWarningIcon';

const columnHelper = createColumnHelper<Attachment>();

function getColumns() {
  return [
    columnHelper.accessor((row) => row.filename, {
      header: 'File Name',
      id: 'filename',
      cell: (info) => {
        const attachment = info.row.original;
        const filename = info.getValue() || `Untitled ${attachment.contentType} ${info.row.index}`;
        return (
          <FileName>
            {attachment.processDocumentAIJob?.isErrored && (
              <ErrorWarningIcon aria-label='File failed to process' role='img' style={{ flexShrink: 0 }} />
            )}
            <DocumentLink entry={attachment} attachableModel='Workflow'>
              <StyledFilename className='first-column' color={colors.blue100}>
                {filename}
              </StyledFilename>
            </DocumentLink>
          </FileName>
        );
      },
    }),
    // No `at <group>` suffix like the admission tab renders: group attribution comes from an
    // activity, and packet documents attach to the workflow itself, never an activity.
    columnHelper.accessor((row) => row.createdByUser?.fullName, {
      id: 'createdByUser',
      header: 'Uploaded By',
      cell: (info) => <div>{info.getValue() || <>&mdash;</>}</div>,
    }),
    columnHelper.accessor((row) => row.createdAt, {
      header: 'Uploaded On',
      id: 'createdAt',
      cell: (info) => <div>{dateAtTime(info.getValue())}</div>,
    }),
  ];
}

type Props = {
  workflowId: string;
};

// The referral packet: every document uploaded to the referral (OLIO-3171). Chat files and the
// benefits-verification PDF can't appear here — the API scopes this endpoint to documents
// attached to the workflow itself, and neither of those ever is.
export default function FilesTab({ workflowId }: Props) {
  const isMobile = useIsMobile();

  const queryParams = useMemo(
    () => ({
      workflowId,
      pageSize: 10,
      include: 'blob,createdByUser,processDocumentAIJob',
      sortBy: 'createdAt desc',
    }),
    [workflowId]
  );

  const query = useInfiniteQuery({
    queryKey: attachmentsQueryKeys.index(queryParams),
    queryFn: ({ pageParam, signal }) => indexWorkflowAttachments({ ...queryParams, page: pageParam as number }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: !!workflowId,
  });

  const files = query.data?.pages.flatMap((page) => page.data) ?? [];
  const columns = useMemo(() => getColumns(), []);

  // Without this, a failed fetch falls through to the table's "No rows found" — which, under a
  // "Files (N)" tab label, reads as the packet being gone rather than a fetch error.
  if (query.isError) {
    return <Body>We couldn’t load this referral’s files. Refresh the page to try again.</Body>;
  }

  return (
    <>
      <ErroredAttachmentsBanner
        attachments={files.filter((attachment) => attachment.processDocumentAIJob?.isErrored)}
        context='referral'
      />
      <DataTable<Attachment>
        data={files}
        columns={columns}
        loading={query.isLoading || query.isFetchingNextPage}
        hasMore={query.hasNextPage}
        onBodyEndReached={() => {
          if (query.hasNextPage) {
            query.fetchNextPage();
          }
        }}
        hasFooter={false}
        maxHeight='none'
        defaultSortBy={[{ id: 'createdAt', desc: true }]}
        columnVisibility={{
          createdByUser: !isMobile,
          createdAt: !isMobile,
        }}
      />
    </>
  );
}

const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledFilename = styled(BodySmallBold)`
  word-break: break-word;
`;
